import { Injectable, signal, computed } from '@angular/core';

export enum LoadingStates {
  UNKNOWN = "UNKNOWN",
  REGISTER = "REGISTER",
  LOGIN = "LOGIN",
  REQUEST_PASSWORD = "REQUEST_PASSWORD",
  RESTORE_PASSWORD = "RESTORE_PASSWORD",
  FA_TOGGLE = "FA_TOGGLE",
  QR_GET = "QR_GET",
  USERS_TOGGLE_ACTIVE = "USERS_TOGGLE_ACTIVE",
  USERS_VERIFICATION_STATUS_CHANGE = "USERS_VERIFICATION_STATUS_CHANGE",
  USERS_GET = "USERS_GET",
  USERS_GET_ITEM = "USERS_GET_ITEM",
  USERS_GET_TYPE = "USERS_GET_TYPE",
  USERS_GET_WORK_FORMATS = "USERS_GET_WORK_FORMATS",
  USERS_EDIT = "USERS_EDIT",
  CATEGORIES_CREATE_EDIT = "CATEGORIES_CREATE_EDIT",
  CATEGORIES_GET = "CATEGORIES_GET",
  MARKETPLACES_CREATE_EDIT = "MARKETPLACES_CREATE_EDIT",
  MARKETPLACES_GET = "MARKETPLACES_GET",
  PLATFORMS_GET = "PLATFORMS_GET",
  BRIEFS_GET = "BRIEFS_GET",
  BRIEFS_GET_ITEM = "BRIEFS_GET_ITEM",
  BRIEFS_CHANGE_STATUS = "BRIEFS_CHANGE_STATUS",
  INTEGRATIONS_GET = "INTEGRATIONS_GET",
  INTEGRATIONS_GET_ITEM = "INTEGRATIONS_GET_ITEM",
  REPORTS_GET = "REPORTS_GET",
  REPORTS_DOWNLOAD_FILE = "REPORTS_DOWNLOAD_FILE",
  REPORTS_CHANGE_REJECT = "REPORTS_CHANGE_REJECT",
  REPORTS_CHANGE_ACCEPT = "REPORTS_CHANGE_ACCEPT",
  DISPUTES_GET = "DISPUTES_GET",
  DISPUTES_GET_ITEM = "DISPUTES_GET_ITEM",
  DISPUTES_GET_CHAT = "DISPUTES_GET_CHAT",
  DISPUTES_CLOSE = "DISPUTES_CLOSE",
  CHATS_WS_CONNECT = "CHATS_WS_CONNECT",
  MESSAGE_DOWNLOAD_FILE = "MESSAGE_DOWNLOAD_FILE",
  CHATS_GET_ITEM = "CHATS_GET_ITEM",
  GET_RECIPIENTS = "GET_RECIPIENTS",
  CHATS_UNREAD_MESSAGES = "CHATS_UNREAD_MESSAGES"
}

export interface LoadingItem {
  id: string;
  type: LoadingStates;
}

@Injectable({
  providedIn: "root",
})
export class LoadingService {
  readonly StatesNames = LoadingStates;

  // Используем Map для более эффективного доступа по ключу
  private loadingStatesMap = signal<Map<LoadingStates, boolean>>(
    new Map(Object.values(LoadingStates).map(state => [state, false]))
  );

  // Храним только активные загрузки
  private activeLoadingsSignal = signal<LoadingItem[]>([]);

  // Публичные API
  // @ts-ignore
  readonly stateIsActive = computed((state: LoadingStates) =>
    this.loadingStatesMap().get(state) || false
  );

  readonly isAnyLoading = computed(() => this.activeLoadingsSignal().length > 0);

  // Метод для проверки конкретного состояния
  isStateLoading(state: LoadingStates): boolean {
    return this.loadingStatesMap().get(state) || false;
  }

  // Метод для проверки нескольких состояний (любое из них активно)
  isAnyStateLoading(states: LoadingStates[]): boolean {
    return states.some(state => this.isStateLoading(state));
  }

  // Добавить загрузку и вернуть её ID
  startLoading(requestType = LoadingStates.UNKNOWN): string {
    const id = this.generateUUID();

    this.activeLoadingsSignal.update(loadings => [
      ...loadings,
      { id, type: requestType }
    ]);

    this.updateStateValue(requestType, true);

    return id;
  }

  // Завершить загрузку по ID
  finishLoading(id: string): void {
    const loadingItem = this.activeLoadingsSignal().find(item => item.id === id);

    if (!loadingItem) return;

    const type = loadingItem.type;

    // Удаляем загрузку
    this.activeLoadingsSignal.update(loadings =>
      loadings.filter(item => item.id !== id)
    );

    // Проверяем, остались ли другие загрузки этого типа
    const hasMoreOfSameType = this.activeLoadingsSignal().some(item => item.type === type);

    if (!hasMoreOfSameType) {
      this.updateStateValue(type, false);
    }
  }

  // Метод для прямого получения значения состояния
  getStateValue(state: LoadingStates): boolean {
    return this.loadingStatesMap().get(state) || false;
  }

  // Вспомогательные методы
  private updateStateValue(state: LoadingStates, value: boolean): void {
    this.loadingStatesMap.update(map => {
      const newMap = new Map(map);
      newMap.set(state, value);
      return newMap;
    });
  }

  private generateUUID(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
